import { SwapRightOutlined, UserOutlined } from '@ant-design/icons';
import { Table, Select, DatePicker, Tag, Pagination } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useData } from 'components/provider/data';
import { useTheme } from 'components/provider/theme';
import dayjs from 'dayjs';
import { API_URL } from 'env';
import { useState, useEffect } from 'react';

const { RangePicker } = DatePicker;

interface Transaction {
    accountName: string;
    login_id: string;
    platform: string;
    symbol: string;
    type: 'Buy' | 'Sell';
    quantity: number;
    price: string;
    total: string;
    pl: number;
    fee: string;
    dateTime: string;
    status: 'Completed' | 'Pending' | 'Failed';
}

const TransactionHistory = ({
    transactionData,
    option = {
        title: true,
        table: true,
        filter: true,
        pagination: true,
        randomize: true,
        border: true,
        shadow: true,
        padding: { top: '20px', bottom: '20px', left: '0', right: '0' },
        randomizeInterval: 1000
    }
}: {
    transactionData?: Transaction[],
    option?: {
        accountLoginId?: string;
        accountName?: string;
        platform?: string;
        title?: boolean;
        table?: boolean;
        filter?: boolean;
        pagination?: boolean;
        randomize?: boolean;
        randomizeInterval?: number;
        border?: boolean;
        shadow?: boolean;
        padding?: { top?: string; bottom?: string; left?: string; right?: string; }
        colWidth?: [string, string, string, string, string, string, string, string, string]
    }
}) => {
    const [data, setData] = useState<Transaction[]>();
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const { theme } = useTheme();
    const [reset, setReset] = useState(0);
    const { setTotalTrade, setTradeHistoryChartData, setAssignedAccountUser } = useData();
    const [closeTimeFrom, setCloseTimeFrom] = useState(dayjs().subtract(30, 'day').startOf('day').toISOString());

    async function fetchAvaliableAccounts() {
        setLoading(true);
        try {
            // Get all accounts from the API
            const accounts = await fetch(`${API_URL}api/v1/be/trade_accounts`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!accounts.ok) {
                console.error('Failed to fetch accounts:', accounts.statusText);
                return [];
            }
            const accountsList = await accounts.json();
            const accountData = accountsList.data.items.map((item: any) => ({
                key: item.id,
                accountName: item.nickname,
                login_id: item.account_number,
                platform: item.platform,
                broker: item.broker,
            }));

            // Fetch transaction history for each account
            let transactionHistory: Transaction[] = [];
            for (const account of accountData) {
                const userResponse = await fetch(`https://fund-forge-api.wealthfarming.org/terminal-accounts?account_number=${account.login_id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                if (userResponse.ok) {
                    const tradeAccountRes = await fetch(
                        `https://fund-forge-api.wealthfarming.org/${account.platform.replace('4', '5')}/page/metrix?account_number=${account.login_id}&close_time_from=*&close_time_to=*`,
                        {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                        }
                    );
                    if (tradeAccountRes.ok) {
                        const accountdata = await tradeAccountRes.json();
                        const dataTrade: Transaction[] = accountdata.metrics.journal_closed_trades.map((trade: any) => ({
                            accountName: account.accountName,
                            login_id: account.login_id,
                            platform: account.platform,
                            symbol: trade.symbol,
                            type: String(trade.type).toLocaleLowerCase() === 'buy' ? 'Buy' : 'Sell',
                            quantity: trade.volume || 0,
                            price: trade.open_price || '0',
                            total: trade.close_price || '0',
                            pl: trade.PnL || 0,
                            fee: trade.commission || '0',
                            dateTime: trade.opentime,
                            status: "Completed",
                        }));
                        transactionHistory = transactionHistory.concat(dataTrade);
                        setData(transactionHistory.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()));
                        setTotalTrade(transactionHistory?.length || 0);
                        setTradeHistoryChartData((prev) => {
                            const filtered = prev.filter(item => item.label !== account.login_id);
                            return [
                                ...filtered,
                                {
                                    label: account.login_id,
                                    name: account.accountName,
                                    data: dataTrade.map((trade) => ({ x: new Date(trade.dateTime), y: trade.pl })),
                                }
                            ];
                        });
                    } else {
                        console.error('Failed to fetch user for account:', account.login_id);
                    }
                } else {
                    console.error('Failed to fetch user for account:', account.login_id);
                }
            }
            return;
        } catch (error) {
            console.error('Error fetching accounts:', error);
            return;
        } finally {
            setLoading(false);
        }
    }

    async function fetchAccountTransactionHistory() {
        setLoading(true);
        try {
            if (!option.accountLoginId || !option.platform) {
                console.error('No account login ID provided');
                return;
            }
            const response = await fetch(`https://fund-forge-api.wealthfarming.org/${option.platform.replace('4', '5')}/page/metrix?account_number=${option.accountLoginId}&close_time_from=${closeTimeFrom}&close_time_to=*`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!response.ok) {
                console.error('Failed to fetch transaction history:', response.statusText);
                return;
            }
            const json = await response.json();
            const transactionHistory: Transaction[] = json.metrics.journal_closed_trades.map((trade: any) => ({
                accountName: trade.account_name || 'N/A',
                login_id: option.accountLoginId,
                platform: option.platform,
                symbol: trade.symbol,
                type: String(trade.type).toLocaleLowerCase() === 'buy' ? 'Buy' : 'Sell',
                quantity: trade.volume || 0,
                price: trade.open_price || '0',
                total: trade.close_price || '0',
                pl: trade.PnL || 0,
                fee: trade.commission || '0',
                dateTime: trade.opentime,
                status: "Completed",
            }));
            setData(transactionHistory.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()));
            setTradeHistoryChartData((prev) => {
                const filtered = prev.filter(item => item.label !== (option.accountLoginId || 'N/A'));
                return [
                    ...filtered,
                    {
                        label: option.accountLoginId || 'N/A',
                        name: option.accountName || 'N/A',
                        data: transactionHistory.map((trade) => ({ x: new Date(trade.dateTime), y: trade.pl })),
                    }
                ];
            });
            setTotalTrade(transactionHistory?.length || 0);
        } catch (error) {
            console.error('Error fetching transaction history:', error);
        } finally {
            setLoading(false);
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!option.accountLoginId) fetchAvaliableAccounts();
        else fetchAccountTransactionHistory();
    }, [closeTimeFrom]);

    const columns: ColumnsType<Transaction> = [
        {
            title: 'Account',
            key: 'account',
            render: (_, item) => (
                <div className="flex items-center gap-2">
                    <UserOutlined className="text-text-muted" />
                    <div>
                        <p className="text-sm font-medium m-0">{option.accountName || item.accountName}</p>
                        <p className="text-xs text-text-muted m-0">#{item.login_id}</p>
                    </div>
                </div>
            ),
        },
        {
            title: 'Platform',
            key: 'platform',
            render: (_, item) => (
                <Tag color={item.platform.toLowerCase().includes('mt') ? 'blue' : 'orange'}>
                    {item.platform}
                </Tag>
            ),
        },
        {
            title: 'Symbol',
            dataIndex: 'symbol',
            render: (val: string) => <span className="text-sm font-medium">{val}</span>,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            render: (val: string) => (
                <Tag color={val === 'Buy' ? 'green' : 'red'}>{val}</Tag>
            ),
        },
        {
            title: 'Qty',
            dataIndex: 'quantity',
            render: (val: number) => <span className="text-sm">{val}</span>,
        },
        {
            title: 'P&L',
            dataIndex: 'pl',
            render: (val: number) => (
                <span className={`text-sm font-semibold ${val > 0 ? 'text-green-500' : val < 0 ? 'text-red-500' : 'text-yellow-500'}`}>
                    {val > 0 ? '+' : ''}${val.toFixed(2)}
                </span>
            ),
        },
        {
            title: 'Date / Time',
            dataIndex: 'dateTime',
            render: (val: string) => (
                <span className="text-xs text-text-secondary">{dayjs(val).format('YYYY-MM-DD HH:mm')}</span>
            ),
        },
    ];

    const pagedData = data?.slice((page - 1) * 10, page * 10) ?? [];

    return (
        <div className="bg-bg-card border border-border rounded-xl shadow-card">
            {option.title && (
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <h2 className="text-base font-semibold m-0">Transaction History</h2>
                    <button
                        onClick={() => setCloseTimeFrom('*')}
                        className="text-sm text-blue-500 hover:underline cursor-pointer flex items-center gap-1"
                    >
                        View all <SwapRightOutlined />
                    </button>
                </div>
            )}

            {option.filter && (
                <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-border">
                    <Select
                        allowClear
                        placeholder="Symbol"
                        className="w-36"
                        options={[
                            { value: 'eur_usd', label: 'EUR/USD' },
                            { value: 'gbp_usd', label: 'GBP/USD' },
                            { value: 'usd_jpy', label: 'USD/JPY' },
                            { value: 'aud_cad', label: 'AUD/CAD' },
                            { value: 'nzd_chf', label: 'NZD/CHF' },
                        ]}
                    />
                    <Select
                        allowClear
                        placeholder="Type"
                        className="w-28"
                        options={[
                            { value: 'buy', label: 'Buy' },
                            { value: 'sell', label: 'Sell' },
                        ]}
                    />
                    <RangePicker
                        className="w-64"
                        placeholder={['From', 'To']}
                        showTime
                    />
                    <Select
                        allowClear
                        placeholder="Status"
                        className="w-32"
                        options={[
                            { value: 'completed', label: 'Completed' },
                            { value: 'pending', label: 'Pending' },
                            { value: 'failed', label: 'Failed' },
                        ]}
                    />
                </div>
            )}

            {option.table && (
                <Table
                    columns={columns}
                    dataSource={pagedData}
                    rowKey={(r, i) => `${r.login_id}-${r.dateTime}-${r.symbol}-${i}`}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    loading={loading}
                    pagination={false}
                    locale={{
                        emptyText: (
                            <div className="py-10 text-center text-text-muted text-sm">
                                No transactions found
                            </div>
                        )
                    }}
                />
            )}

            {option.pagination && (
                <div className="flex justify-end px-5 py-3 border-t border-border">
                    <Pagination
                        current={page}
                        total={data?.length ?? 0}
                        pageSize={10}
                        showSizeChanger={false}
                        showTotal={total => `Total ${total} transactions`}
                        onChange={setPage}
                    />
                </div>
            )}
        </div>
    );
};

export default TransactionHistory;
export type { Transaction };
